import React, { useState } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';

import Spacer from '../common/Spacer';
import { validateEmail, validateLength } from '../../utils/validation';
import { sendSignUpRequest } from '../../utils/api/accountApi';
import { genericErrorMessage } from '../../utils/lang/commonErrors';

import { isApiError } from '../../utils/api/clearApiClient';
import EmailInput from './fields/EmailInput';
import PasswordInput from './fields/PasswordInput';
import Checkbox from './fields/Checkbox';
import SubmitButton from './fields/SubmitButton';
import ReCaptchaDisclaimer from './fields/reCaptchaDisclaimer';

import styles from './SignUpForm.scss';

const SignUpForm = () => {
  const history = useHistory();

  const [emailValue, setEmailValue] = useState('');
  const onEmailChange = (e) => setEmailValue(e.target.value);

  const [passValue, setPassValue] = useState('');
  const onPassChange = (e) => setPassValue(e.target.value);

  const [subsValue, setSubsValue] = useState(false);
  const onSubsChange = () => setSubsValue(!subsValue);

  const [isLoading, setLoading] = useState(false);

  const [formErrors, setFormErrors] = useState({});

  const intl = useIntl();

  const onSubmit = () => {
    setLoading(true);
    const emailError = validateEmail(emailValue);
    const passError = validateLength(passValue, 8, 21);

    if (!emailError && !passError) {
      sendSignUpRequest(emailValue, passValue, subsValue).then(
        () => {
          history.push('/profile');
        },
        (err) => {
          if (isApiError(err)) {
            if (err.response.data.code === 6) {
              history.push(`/enter/email/already-used?email=${encodeURIComponent(emailValue)}`);
            } else if (err.code === 3 && err.message === 'email is invalid') {
              setFormErrors({
                email: intl.formatMessage({
                  id: 'signup.error.emailDoesNotExist',
                  defaultMessage: 'Email address doesn’t exist',
                  description:
                    'Error message show as the result of a failed check of the inputted email against email verification service',
                }),
              });
            } else {
              setFormErrors({
                pass: intl.formatMessage(genericErrorMessage),
              });
            }
          } else {
            console.error('Sign Up request failed', err);
            setFormErrors({
              pass: intl.formatMessage(genericErrorMessage),
            });
          }

          setLoading(false);
        },
      );
    } else {
      setFormErrors({
        email: emailError,
        pass: passError,
      });
      setLoading(false);
    }
  };

  return (
    <form className={styles.form}>
      <h2 className={styles.title}>
        <FormattedMessage id="signUp.title" defaultMessage="Create account" description="Title of the sign-up page" />
      </h2>
      <p className={styles.subtitle}>
        <FormattedMessage
          id="signUp.subtitle"
          defaultMessage="If you already have an account, please, <link>Log In</link>"
          description="Subtitle of the sign-up page"
          values={{
            link: (chunk) => <Link to="/enter/login">{chunk}</Link>,
          }}
        />
      </p>

      <EmailInput value={emailValue} onChange={onEmailChange} error={formErrors.email} />

      <PasswordInput
        passValue={passValue}
        onChange={onPassChange}
        emailValue={emailValue}
        error={formErrors.pass}
        label={intl.formatMessage({
          id: 'signUp.form.passwordLabel',
          defaultMessage: 'Create password',
          description: 'Label of the password field within the sign up form',
        })}
        hideEmptyError
      />
      {!formErrors.pass && <Spacer height={20} />}

      <Checkbox
        onChange={onSubsChange}
        label={intl.formatMessage({
          id: 'signUp.form.subscribeToNewsletterCheckbox',
          defaultMessage: 'Send me useful tips, special offers and updates',
          description: 'Label of the checkbox; if checked user will be subscribed to email newsletter delivery',
        })}
        value={subsValue}
        isNoError
      />
      <Spacer height={35} />

      <SubmitButton
        text={intl.formatMessage({
          id: 'signUp.form.submitButton',
          defaultMessage: 'Create',
          description: 'Content of the submit button of the sign-up form',
        })}
        onSubmit={onSubmit}
        isLoading={isLoading}
        className={styles.cta}
      />

      <ReCaptchaDisclaimer />
    </form>
  );
};

export default SignUpForm;
